import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ethers } from "ethers";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSWRConfig } from "swr";
import { useToast } from "../../hooks/toast";
import useWallet from "../../hooks/wallet/useWallet";

import { AuthContext } from "../../contexts/AuthContext";
import { ConnectWalletContext } from "../../contexts/ConnectWalletContext";
import {
  ChainIdEnum,
  UNIFIED_VAULT_CHAIN_IDS,
} from "../../enums/chain";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useClock } from "../../hooks/api/clock/useClock";
import { useInviteStatus } from "../../hooks/api/invite/useInviteStatus";
import { useRegister } from "../../hooks/api/register/useRegister";
import { useYieldVault } from "../../hooks/api/yieldVault/useYieldVault";
import useDepositERC20OrETH from "../../hooks/contracts/useCollateralAssetOrETH";

import { DepositWithdrawCollaterals } from "../../constants/addresses";
import { PERPETUAL_DEX_ADDRESSES } from "../../constants/perpetual-dex/addresses";
import { useCheckReferral } from "../../hooks/api/referrals/useCheckReferral";
import useDeposit from "../../hooks/contracts/useDeposit";
import { useUnifiedVault } from "../../hooks/perpetual-dex/useUnifiedVault";
import { useSFX } from "../../hooks/useSFX";
import { ISignature } from "../../interfaces/Signing";
import { depositMaxResult } from "../../utils/deposit";
import { supportedSigningChainIds } from "../../utils/wallet/connectors";
import { IConnectWalletType } from "./ConnectWallet";
import { EIP6963ProviderDetail } from "../../hooks/wallet/useEIP6963";
import { ConnectWalletPageEnum } from "./types";

export function useModalSetup() {
  const { t } = useTranslation("app", {
    keyPrefix: "ConnectWalletModal.ConnectWalletModal",
  });
  const { mutate } = useSWRConfig();

  const referralCode = window.localStorage.getItem(
    LocalStorageKeyEnum.REFERRAL_CODE
  );

  const wallet = useWallet();
  const {
    activate,
    isWrongNetwork,
    setChainToSwitch,
    chainId,
    connectedWallet,
    hasBrowserWallet,
  } = wallet;

  const {
    accountSigningKeyState,
    accountApiKeyState,
    showInvalidRefModal,
    account,
    getImportedAccountAddress,
    reconnectImportedAccount,
    isAccountImported,
    isGeoblocked,
  } = useContext(AuthContext);

  const { data: referralData } = useCheckReferral();
  const { data: inviteStatus, mutate: mutateInviteStatus } = useInviteStatus();
  const { getTimestamp } = useClock();
  const { registerSigningKey } = useRegister();
  const { addToast } = useToast();
  const depositForm = useForm({
    mode: "onChange",
  });

  const [showReadyToDepositMessage, setShowReadyToDepositMessage] =
    useState(false);
  const [assetToDeposit, setAssetToDeposit] =
    useState<DepositWithdrawCollaterals>(DepositWithdrawCollaterals.Usdc);
  const [assetAllowance, setAssetAllowance] = useState<string>();

  const { data: yieldVaultData } = useYieldVault();
  const { data: accountData, showOnboarding } = useGetAccount();
  const {
    l1Contract,
    l2Address,
    getAllowance,
    getAssetPermitSignature,
    approveAsset,
    userBalance,
    ethBalance,
    decimals,
  } = useDepositERC20OrETH(wallet, assetToDeposit);
  const {
    l1DepositHelper,
    depositERC20,
    depositERC20WithPermit,
    depositETH,
    depositERC20OptimismOrArbitrum,
    depositETHOptimismOrArbitrum,
    approvalSpender,
    depositFees,
  } = useDeposit(wallet, assetToDeposit);

  // UnifiedVault deposit for BASECAMP_TESTNET
  const {
    approveUSDC: approveUnifiedVaultUSDC,
    deposit: depositToUnifiedVault,
    isApproved: isUnifiedVaultApproved,
    isNativeDepositAsset,
  } = useUnifiedVault(wallet, assetToDeposit, decimals);

  // Check if we should use UnifiedVault deposit (perpetual DEX chains)
  const useUnifiedVaultFlow = useMemo(() => {
    return (
      UNIFIED_VAULT_CHAIN_IDS.includes(chainId as ChainIdEnum) &&
      PERPETUAL_DEX_ADDRESSES[chainId as ChainIdEnum] !== undefined
    );
  }, [chainId]);

  // DEPOSIT
  const [selectedDepositChain, setSelectedDepositChain] = useState(
    supportedSigningChainIds[0]
  );

  const { playSound } = useSFX();
  const depositAmount = useWatch({
    control: depositForm.control,
    name: "amount",
  });
  const depositBalance = useMemo(
    () =>
      useUnifiedVaultFlow && isNativeDepositAsset ? ethBalance : userBalance,
    [ethBalance, isNativeDepositAsset, useUnifiedVaultFlow, userBalance]
  );

  const depositAssetBalance = useMemo(
    () =>
      depositBalance
        ? ethers.utils.formatUnits(depositBalance, decimals)
        : undefined,
    [decimals, depositBalance]
  );

  // The gas-reserved maximum, and the reason when there is none.
  const depositMax = useMemo(
    () =>
      depositMaxResult({
        balance: depositBalance,
        nativeBalance: ethBalance,
        decimals,
        chainId: chainId as ChainIdEnum | undefined,
        asset: assetToDeposit,
      }),
    [assetToDeposit, chainId, decimals, depositBalance, ethBalance]
  );
  const maxDepositBalance = depositMax.ok ? depositMax.max : undefined;
  const maxDepositUnavailable = depositMax.ok ? undefined : depositMax.reason;

  const [approvedAmountAndSignature, setApprovedAmountAndSignature] = useState<{
    approvedAmount: string | undefined;
    signature: ISignature | undefined;
    deadline: number;
  }>({
    approvedAmount: undefined,
    signature: undefined,
    deadline: 0,
  });
  const [showHowDepositsWork, setShowHowDepositsWork] = useState(false);
  const [pageError, setPageError] = useState("");
  const [depositError, setDepositError] = useState("");

  // Clear any deposit error as soon as the user edits the amount,
  // so a prior cancellation / failure message doesn't linger over the new input.
  useEffect(() => {
    if (pageError || depositError) {
      setPageError("");
      setDepositError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositAmount]);

  const {
    yieldVaultToggle,
    setYieldVaultToggle,
    connectWalletFlow,
    depositAsset,
  } = useContext(ConnectWalletContext);
  const directDepositFlow = connectWalletFlow === "deposit";
  const [selectedWallet, setSelectedWallet] = useState<IConnectWalletType>();
  const [selectedEIP6963Provider, setSelectedEIP6963Provider] = useState<EIP6963ProviderDetail>();
  const [inviteCode, setInviteCode] = useState(
    window.localStorage.getItem(LocalStorageKeyEnum.ACCESS_CODE) || ""
  );
  const connectWalletInFlightRef = useRef(false);
  const registerInFlightRef = useRef(false);
  const depositActionInFlightRef = useRef(false);
  const lastRegisterSuccessRef = useRef<{
    account: string;
    timestamp: number;
  } | null>(null);

  const setWallet = useCallback((wallet: IConnectWalletType, eip6963Provider?: EIP6963ProviderDetail) => {
    setSelectedWallet(wallet);
    setSelectedEIP6963Provider(eip6963Provider);
  }, []);

  const [availablePages, setAvailablePages] = useState<ConnectWalletPageEnum[]>(
    [
      ConnectWalletPageEnum.ONBOARD,
      ConnectWalletPageEnum.CONNECT_WALLET,
      ConnectWalletPageEnum.SWITCH_NETWORK,
      ConnectWalletPageEnum.ENABLE_TRADING_SIGN,
      ConnectWalletPageEnum.DEPOSIT,
    ]
  );

  useEffect(() => {
    if (directDepositFlow) {
      setAssetToDeposit(depositAsset ?? DepositWithdrawCollaterals.Usdc);
    }
  }, [depositAsset, directDepositFlow]);

  return {
    t, mutate, referralCode, wallet, activate, isWrongNetwork,
    setChainToSwitch, chainId, connectedWallet, hasBrowserWallet,
    accountSigningKeyState, accountApiKeyState, showInvalidRefModal, account,
    getImportedAccountAddress, reconnectImportedAccount, isAccountImported,
    isGeoblocked, referralData, inviteStatus, mutateInviteStatus, getTimestamp,
    registerSigningKey, addToast, depositForm, showReadyToDepositMessage,
    setShowReadyToDepositMessage, assetToDeposit, setAssetToDeposit,
    assetAllowance, setAssetAllowance, yieldVaultData, accountData,
    showOnboarding, l1Contract, l2Address, getAllowance, getAssetPermitSignature,
    approveAsset, userBalance, ethBalance, decimals, l1DepositHelper,
    depositERC20, depositERC20WithPermit, depositETH,
    depositERC20OptimismOrArbitrum, depositETHOptimismOrArbitrum, approvalSpender,
    depositFees, approveUnifiedVaultUSDC, depositToUnifiedVault,
    isUnifiedVaultApproved, isNativeDepositAsset, useUnifiedVaultFlow,
    selectedDepositChain, setSelectedDepositChain, playSound, depositAmount,
    depositBalance, depositAssetBalance, maxDepositBalance, maxDepositUnavailable,
    approvedAmountAndSignature, setApprovedAmountAndSignature, showHowDepositsWork,
    setShowHowDepositsWork, pageError, setPageError, depositError, setDepositError,
    yieldVaultToggle, setYieldVaultToggle, connectWalletFlow, depositAsset,
    directDepositFlow, selectedWallet, setSelectedWallet, selectedEIP6963Provider,
    setSelectedEIP6963Provider, inviteCode, setInviteCode, connectWalletInFlightRef,
    registerInFlightRef, depositActionInFlightRef, lastRegisterSuccessRef,
    setWallet, availablePages, setAvailablePages,
  };
}
